import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { WBSButton } from '@/common/WBSButton';
import type { Student, StudentBoardAction } from '~/types/studentTypes';

type ActionsCellProps = {
  actions: StudentBoardAction[];
  row: Student;
  onAdopt?: (studentId: string) => void | Promise<void>;
  onPassOn?: (studentId: string) => void | Promise<void>;
  onEncourage?: (studentId: string) => void | Promise<void>;
  onAssign?: (studentId: string) => void | Promise<void>;
};

/**
 * One actions cell for both student boards.
 *
 * `actions` is the server's per-row list now (`StudentBoardItem.actions`), so
 * this component branches on nothing: it renders what it is given, disabled
 * where the server says disabled, and IGNORES an action name it has no button
 * for. That last clause is load-bearing — it is what let W3.6 add `assign_to`
 * with no schema change, and it must survive for whatever is added next.
 *
 * Legacy renders the same action set in two shapes: the group tab passes
 * `use_action_row => true` (`buddypress/groups/single/student-board.php:6`) and
 * the site-wide page has that meta commented out
 * (`page-student-board.php:27`) — one component, two mount points.
 */
export function ActionsCell({
  actions,
  row,
  onAdopt,
  onPassOn,
  onEncourage,
  onAssign,
}: ActionsCellProps) {
  const { t } = useTranslation();

  return (
    <ActionsCellContainer>
      {actions.map(({ action, disabled, count }) => {
        switch (action) {
          case 'encourage': {
            // The received count rides inside the label ("Encourage (2)" /
            // "Encouraged (2)") — SendEncouragement.php:67-101. The disabled
            // state means this helper already encouraged them, or the student
            // hit the cap of 3 (EncouragementModel.php:26-34), and legacy
            // renders that as a `no-click` pill rather than a live button
            // (EncourageStudentInteractivePopup.php:43-46) — so the handler is
            // wired on the enabled branch only.
            const label = disabled ? t('wbs.encouraged') : t('wbs.encourage');
            return (
              <WBSButton
                key={action}
                alternate
                disabled={disabled}
                onClick={
                  disabled ? undefined : () => void onEncourage?.(row.id)
                }
              >
                {count > 0 ? `${label} (${count})` : label}
              </WBSButton>
            );
          }
          case 'adopt':
            // Disabled renders legacy's no-click "Not Adoptable" pill
            // (Adopt.php:15-22), and it is not a dead branch. Re-measured
            // 2026-08-18 against wbs_production, bounded to the MIGRATED rows
            // (user id <= 2634; the next id after 2634 is 6617, and everything
            // above it is locally seeded by pytest/e2e, which is what made the
            // earlier unbounded figure underivable): 4,468 of 12,160
            // helper x eligible-row pairs -- 95 helpers x 128 board-eligible
            // students -- fail can_student_be_adopted_by. The failing arm is
            // almost all study-helper language preference (3,730 pairs) plus
            // gender (912); the under-18 age rule fails 0.
            return disabled ? (
              <WBSButton key={action} disabled>
                {t('wbs.not-adoptable')}
              </WBSButton>
            ) : (
              <WBSButton key={action} onClick={() => onAdopt?.(row.id)}>
                {t('wbs.adopt')}
              </WBSButton>
            );
          case 'pass_on':
            return (
              <WBSButton
                key={action}
                alternate
                disabled={disabled}
                onClick={() => onPassOn?.(row.id)}
              >
                {t('wbs.pass-on')}
              </WBSButton>
            );
          case 'assign_to':
            // The server emits this only where the caller ADMINs a
            // congregation or custom study group that currently claims the
            // student (`AssignToAction.php:14-20,32-56`), and never disabled —
            // legacy has no disabled form of this action at all.
            return (
              <WBSButton
                key={action}
                alternate
                disabled={disabled}
                onClick={() => onAssign?.(row.id)}
              >
                {t('wbs.assign-to')}
              </WBSButton>
            );
          default:
            // Anything added later: the server may send it, this cell simply
            // has no button for it yet.
            return null;
        }
      })}
    </ActionsCellContainer>
  );
}

const ActionsCellContainer = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;
  justify-content: flex-end;
`;
